import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CreditCard, CheckCircle, AlertCircle, Store, Building, Banknote, Receipt } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ApplicationDetails {
  id: string;
  business_name: string;
  contact_name: string;
  contact_email: string;
  payment_amount: number;
  payment_status: string;
  pta_id: string;
  events: {
    name: string;
    event_date: string;
  } | null;
  ptas: {
    id: string;
    name: string;
  } | null;
}

interface PaymentSettings {
  stripe_enabled: boolean;
  bacs_enabled: boolean;
  cash_enabled: boolean;
  cheque_enabled: boolean;
  bacs_account_number: string | null;
  bacs_sort_code: string | null;
  cheque_payable_to: string | null;
  cheque_address: string | null;
  cash_instructions: string | null;
}

export default function StallPayment() {
  const { applicationId } = useParams<{ applicationId: string }>();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [application, setApplication] = useState<ApplicationDetails | null>(null);
  const [settings, setSettings] = useState<PaymentSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentProvider, setPaymentProvider] = useState<string | null>(null);

  useEffect(() => {
    if (applicationId) {
      fetchApplicationDetails();
    }
  }, [applicationId]);

  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      toast({
        title: "Payment Successful",
        description: "Your stall fee payment has been received. Thank you!",
      });
      fetchApplicationDetails();
    } else if (paymentStatus === 'cancelled') {
      toast({
        title: "Payment Cancelled",
        description: "Your payment was cancelled. You can try again when ready.",
        variant: "destructive"
      });
    }
  }, [searchParams]);

  const fetchApplicationDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('stall_applications')
        .select(`
          id,
          business_name,
          contact_name,
          contact_email,
          payment_amount,
          payment_status,
          pta_id,
          events (name, event_date),
          ptas (id, name)
        `)
        .eq('id', applicationId)
        .single();

      if (fetchError) {
        throw new Error('Application not found');
      }

      const appData = data as unknown as ApplicationDetails;
      setApplication(appData);

      // Detect payment provider
      if (appData.pta_id) {
        const { data: provider } = await supabase
          .rpc('get_pta_payment_provider', { p_pta_id: appData.pta_id });
        setPaymentProvider(provider);
      }

      // Fetch payment settings for the PTA
      const { data: settingsData } = await supabase
        .from('stall_application_settings')
        .select('stripe_enabled, bacs_enabled, cash_enabled, cheque_enabled, bacs_account_number, bacs_sort_code, cheque_payable_to, cheque_address, cash_instructions')
        .eq('pta_id', data.pta_id)
        .single();

      if (settingsData) {
        setSettings(settingsData as PaymentSettings);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load application details');
    } finally {
      setLoading(false);
    }
  };

  const handleOnlinePayment = async () => {
    if (!applicationId || !application) return;

    setPaying(true);
    try {
      if (paymentProvider === 'mollie') {
        const { data, error: payError } = await supabase.functions.invoke('mollie-create-payment', {
          body: {
            ptaId: application.pta_id,
            paymentType: 'stall_payment',
            amount: application.payment_amount,
            currency: 'GBP',
            description: `Stall Fee: ${application.events?.name || 'Event'} - ${application.business_name}`,
            referenceId: applicationId,
          }
        });
        if (payError) throw payError;
        if (data?.checkoutUrl) {
          window.location.href = data.checkoutUrl;
          return;
        }
      } else if (paymentProvider === 'stripe') {
        const { data, error: payError } = await supabase.functions.invoke('create-stall-payment-checkout', {
          body: { applicationId }
        });
        if (payError) throw payError;
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
      } else {
        throw new Error('No online payment provider configured');
      }
    } catch (err: any) {
      toast({
        title: "Payment Error",
        description: err.message || "Could not initiate payment. Please try again.",
        variant: "destructive"
      });
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Application Not Found</h2>
              <p className="text-muted-foreground">
                {error || 'The application you are looking for does not exist or has been removed.'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isPaid = application.payment_status === 'paid';
  const hasOnlinePayment = !!paymentProvider;
  const hasOfflinePayment = settings && (settings.bacs_enabled || settings.cash_enabled || settings.cheque_enabled);
  const hasPaymentMethods = hasOnlinePayment || hasOfflinePayment;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-background p-4 flex items-center justify-center">
      <Card className="max-w-lg w-full shadow-lg">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Store className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Stall Fee Payment</CardTitle>
          <CardDescription>
            {application.ptas?.name}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Application Details */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-lg">{application.business_name}</h3>
            <p className="text-sm text-muted-foreground">
              Contact: {application.contact_name}
            </p>
            {application.events && (
              <div className="text-sm">
                <p>Event: {application.events.name}</p>
                <p className="text-muted-foreground">
                  Date: {new Date(application.events.event_date).toLocaleDateString('en-GB', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Payment Status */}
          {isPaid ? (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-center">
              <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <p className="font-semibold text-green-800 dark:text-green-400">Payment Complete</p>
              <p className="text-sm text-green-700 dark:text-green-500">
                Thank you! Your payment of £{application.payment_amount?.toFixed(2)} has been received.
              </p>
            </div>
          ) : (
            <>
              {/* Payment Amount */}
              <div className="text-center py-4 border-y border-border">
                <p className="text-sm text-muted-foreground mb-1">Amount Due</p>
                <p className="text-4xl font-bold text-primary">
                  £{application.payment_amount?.toFixed(2)}
                </p>
              </div>

              {/* Payment Options */}
              {!hasPaymentMethods ? (
                <div className="text-center text-muted-foreground">
                  <p>Payment methods have not been configured yet.</p>
                  <p className="text-sm">Please contact the PTA for payment instructions.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Online Payment */}
                  {hasOnlinePayment && (
                    <Button
                      onClick={handleOnlinePayment}
                      disabled={paying}
                      className="w-full h-12 text-lg"
                      size="lg"
                    >
                      {paying ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="h-5 w-5 mr-2" />
                          Pay by Card
                        </>
                      )}
                    </Button>
                  )}

                  {/* BACS Payment */}
                  {settings?.bacs_enabled && settings.bacs_account_number && (
                    <div className="border border-border rounded-lg p-4 space-y-2">
                      <div className="flex items-center gap-2 font-semibold">
                        <Building className="h-5 w-5 text-primary" />
                        Bank Transfer (BACS)
                      </div>
                      <div className="text-sm space-y-1 bg-muted/50 rounded p-3">
                        <p><span className="text-muted-foreground">Sort Code:</span> {settings.bacs_sort_code}</p>
                        <p><span className="text-muted-foreground">Account Number:</span> {settings.bacs_account_number}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          Please use your business name as the payment reference.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Cash Payment */}
                  {settings?.cash_enabled && (
                    <div className="border border-border rounded-lg p-4 space-y-2">
                      <div className="flex items-center gap-2 font-semibold">
                        <Banknote className="h-5 w-5 text-primary" />
                        Cash Payment
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {settings.cash_instructions || 'Please bring cash payment on the day of the event.'}
                      </p>
                    </div>
                  )}

                  {/* Cheque Payment */}
                  {settings?.cheque_enabled && settings.cheque_payable_to && (
                    <div className="border border-border rounded-lg p-4 space-y-2">
                      <div className="flex items-center gap-2 font-semibold">
                        <Receipt className="h-5 w-5 text-primary" />
                        Cheque Payment
                      </div>
                      <div className="text-sm space-y-1">
                        <p><span className="text-muted-foreground">Make payable to:</span> {settings.cheque_payable_to}</p>
                        {settings.cheque_address && (
                          <p><span className="text-muted-foreground">Send to:</span> {settings.cheque_address}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {hasOnlinePayment && (
                <p className="text-xs text-center text-muted-foreground">
                  Secure payment processed directly to {application.ptas?.name}.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
